//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from '../common/service';
import FirebasePushNotificationsModel from './PushNotification.model';

/**
 * @service FirebasePushNotificationService
 * @description Service for managing Firebase push notification records within the application.
 * Unlike the messaging service which handles real-time delivery, this service is responsible for
 * retrieving the history or persistent records of push notifications stored in the backend database.
 *
 * It interacts with the `/pushnotification/notification/` endpoint.
 *
 * **Key Responsibilities:**
 * 1. **History Retrieval**: Fetching past notifications for a user or business.
 * 2. **Data Access**: Providing a typed interface for notification data.
 * 3. **Resource Management**: Handling HTTP request lifecycles.
 *
 * @feature 1. Retrieve notifications for the logged-in user (receiver).
 * @feature 2. Retrieve notifications for a specific business context.
 * @feature 3. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<FirebasePushNotificationsModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /pushnotification/notification/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})

export class FirebasePushNotificationService extends Service<FirebasePushNotificationsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/pushnotification/notification/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/pushnotification/notification/');
  }

  /**
   * @method getAllPushNotificationsByReceiverID
   * @async
   * @description Fetch push notifications for the currently authenticated receiver.
   *
   * Retrieves a list of notifications (limit 100, ordered by new).
   * The backend likely infers the receiver ID from the authentication token.
   *
   * @returns {Promise<Array<FirebasePushNotificationsModel>>} Promise resolving to an array of notification models.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getAllPushNotificationsByReceiverID(): Promise<Array<FirebasePushNotificationsModel>> {
    return new Promise<Array<FirebasePushNotificationsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<FirebasePushNotificationsModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=100&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request); // Keep track of the ongoing request for potential cancellation or cleanup.
    });
  }

  /**
   * @method getAllPushNotificationsByReceiverBusinessID
   * @async
   * @description Fetch push notifications associated with a specific business ID.
   *
   * @param {string} receiverBusinessId - The unique identifier of the business receiving notifications.
   * @returns {Promise<Array<FirebasePushNotificationsModel>>} Promise resolving to an array of notification models.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getAllPushNotificationsByReceiverBusinessID(receiverBusinessId: string): Promise<Array<FirebasePushNotificationsModel>> {
    return new Promise<Array<FirebasePushNotificationsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<FirebasePushNotificationsModel>>(`${this.baseUrl}${this.serviceUrl}?receiverBusinessID=${receiverBusinessId}&pageSize=100&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request); // Keep track of the ongoing request for potential cancellation or cleanup.
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
